import React, { useEffect, useState } from "react";
import { Box, Flex, Grid, Text, useToken } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import SearchInput from "@/shared/components/search-input";
import CustomNoResults from "@/shared/components/no-results/CustomNoResults";
import CenteredSpinner from "@/shared/components/centered-spinner";
import CustomIcon from "../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import PathPickPagination from "@/features/pathpick/components/ui/PathPickPagination";
import PathPickFilterToggleButton from "@/features/pathpick/components/ui/PathPickFilterToggleButton";
import PathPickOrderHistoryDateRangeFields from "@/features/pathpick/features/order-history/components/PathPickOrderHistoryDateRangeFields";
import PathPickOrderHistoryListItem from "@/features/pathpick/features/order-history/components/PathPickOrderHistoryListItem";
import useGetPathPickOrders from "@/features/pathpick/features/order-history/hooks/query/useGetPathPickOrders";
import usePathPickOrderHistoryFilter from "@/features/pathpick/features/order-history/hooks/usePathPickOrderHistoryFilter";
import { toPathPickOrderEndOfDay } from "@/features/pathpick/features/order-history/utils";
import { PathPickOrder } from "@/features/pathpick/features/order-history/types/response";
import {
  ORDER_HISTORY_TEMPLATE_COLUMNS,
  ORDER_STATUS_FILTERS,
} from "@/features/pathpick/features/order-history/constants";
import { Helmet } from "react-helmet-async";

// 참조 안정성 확보용 (빈 응답마다 새 배열을 만들지 않는다)
const EMPTY_ORDERS: PathPickOrder[] = [];

function PathPickOrderHistoryPage() {
  const { t, tTitle } = useI18nHelper();
  const pageTitle = `${tTitle("common:navigation.path_pick")} | ${tTitle(
    "common:navigation.path_pick_order_history"
  )}`;
  const { navigate } = useRouterHelper();
  const [hairlineDefault] = useToken("shadows", ["hairlineDefault"]);

  const {
    statusGroup,
    query,
    startDate,
    endDate,
    pageNumber,
    pageSize,
    changeStatusGroup,
    applyQuery,
    changeStartDate,
    changeEndDate,
    clearDateRange,
    setPageNumber,
    setPageSize,
  } = usePathPickOrderHistoryFilter();

  // 검색 입력 초안 — 확정된 검색어(query)만 쿼리스트링으로 관리한다.
  const [searchValue, setSearchValue] = useState<string>(query ?? "");
  // 모바일 기간 필터 펼침 — 값은 위 훅(쿼리스트링)이 소유하고 이 상태는 접힘 여부뿐이다.
  const [isDateFilterOpen, setIsDateFilterOpen] = useState<boolean>(false);

  // 쿼리스트링(query)이 외부에서 바뀌면(뒤로가기, URL 직접 입력 등) 입력창을 동기화한다.
  useEffect(() => {
    setSearchValue(query ?? "");
  }, [query]);

  const { pathPickOrderList, isPending } = useGetPathPickOrders({
    // ALL 탭은 파라미터를 아예 보내지 않는다.
    statusGroup: statusGroup === "ALL" ? undefined : statusGroup,
    searchWord: query,
    orderedAt__gte: startDate,
    orderedAt__lte: endDate ? toPathPickOrderEndOfDay(endDate) : undefined,
    pageNumber,
    pageSize,
  });

  const orders = pathPickOrderList?.orders ?? EMPTY_ORDERS;
  const total = pathPickOrderList?.totalCount ?? 0;
  const statusCounts = pathPickOrderList?.statusCounts;
  // 기간은 필터 축 하나로 센다(시작일·종료일을 따로 세지 않는다).
  const isDateFilterActive = Boolean(startDate || endDate);

  const handleSearch = () => {
    applyQuery(searchValue.trim() || undefined);
  };

  return (
    <Box
      sx={{
        padding: "16px",
        overflow: "auto",
        height: "100%",
        paddingBottom: "100px",
      }}
    >
      <Helmet>
        <title>{pageTitle}</title>
        <meta property="og:title" content={pageTitle} />
      </Helmet>
      <Flex flexDirection={"column"} gap={"16px"}>
        <Text textStyle={"body-lg-bold"} color={"#1D1D1B"}>
          {t("path_pick:order_history.title")}
        </Text>

        <Flex
          flexDirection={"column"}
          gap={"12px"}
          padding={"16px"}
          boxShadow={hairlineDefault}
          borderRadius={"8px"}
          background={"#FFFFFF"}
        >
          {/* 상태 필터 버튼 — 라벨 + statusCounts 건수.
              이 화면의 1차 축이라 모바일에서도 감추지 않고, 좁은 폭에서는
              세 줄로 접히는 대신 한 줄 가로 스크롤로 둔다. */}
          <Flex
            gap={"6px"}
            flexWrap={{ base: "nowrap", md: "wrap" }}
            overflowX={{ base: "auto", md: "visible" }}
            sx={{
              "::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            {ORDER_STATUS_FILTERS.map((filter) => (
              <AppButton
                key={filter.value}
                variant={statusGroup === filter.value ? "primary" : "white"}
                onClick={() => changeStatusGroup(filter.value)}
                chakraProps={{
                  padding: "10px",
                  flexShrink: 0,
                }}
              >
                <Text textStyle={"body-sm-bold"} whiteSpace={"nowrap"}>
                  {`${t(filter.labelKey)} ${statusCounts?.[filter.value] ?? 0}`}
                </Text>
              </AppButton>
            ))}
          </Flex>

          {/* 검색 + 날짜 */}
          <Flex gap={"8px"} alignItems={"center"} flexWrap={"wrap"}>
            <Flex
              gap={"8px"}
              alignItems={"center"}
              flexWrap={"wrap"}
              minWidth={0}
            >
              <SearchInput
                // SearchInput은 내부에 flexShrink 0이 박혀 있어 폭을 직접 클램프한다.
                width={"min(320px, 100%)"}
                value={searchValue}
                onChange={setSearchValue}
                onKeyDown={handleSearch}
                placeholder={t("path_pick:order_history.search_placeholder")}
                inputProps={{
                  border: "0.5px solid #C0C0C0",
                  _hover: { borderColor: "#1272EF" },
                  height: "32px",
                }}
              />
              <AppButton
                variant={"primary"}
                width={"80px"}
                onClick={handleSearch}
              >
                <Flex gap={"4px"} alignItems={"center"}>
                  <CustomIcon
                    src={"search"}
                    width={16}
                    height={16}
                    color={"#FFFFFF"}
                  />
                  <Text textStyle={"body-sm-bold"}>
                    {tTitle("common:search")}
                  </Text>
                </Flex>
              </AppButton>
              <Text textStyle={"body-sm-medium"}>
                {t("path_pick:order_history.search_result", { count: total })}
              </Text>
              {/* 트리거 버튼이 자체적으로 base에서만 보이도록 분기한다 */}
              <PathPickFilterToggleButton
                appliedCount={isDateFilterActive ? 1 : 0}
                isExpanded={isDateFilterOpen}
                onClick={() => setIsDateFilterOpen((prev) => !prev)}
              />
            </Flex>

            {/* 좁은 폭에서는 이 줄이 세로를 두 줄 더 먹어 목록을 밀어낸다 —
                모바일에서는 같은 값을 아래 펼침 영역에서 고른다. */}
            <Flex
              display={{ base: "none", md: "flex" }}
              gap={"8px"}
              alignItems={"center"}
              marginLeft={"auto"}
            >
              <Text textStyle={"body-sm-regular"} color={"#6F6F6C"}>
                {`${t("path_pick:order_history.date_range")} : `}
              </Text>
              <PathPickOrderHistoryDateRangeFields
                startDate={startDate}
                endDate={endDate}
                onChangeStartDate={changeStartDate}
                onChangeEndDate={changeEndDate}
              />
            </Flex>
          </Flex>

          {/* 모바일 기간 필터 — 위 [필터] 버튼으로 접었다 편다.
              달력은 모달 안이 아니라 페이지 위에 떠야 한다(react-datepicker의 팝퍼를
              Chakra Modal 안에 넣으면 오버레이가 겹치고 닫힘 제어가 어긋난다). */}
          <Flex
            display={{ base: isDateFilterOpen ? "flex" : "none", md: "none" }}
            flexDirection={"column"}
            gap={"8px"}
            padding={"12px"}
            borderRadius={"8px"}
            background={"#F7F9FA"}
            boxShadow={hairlineDefault}
          >
            <Flex
              justifyContent={"space-between"}
              alignItems={"center"}
              gap={"8px"}
            >
              <Text textStyle={"body-sm-bold"} color={"#444440"}>
                {t("path_pick:order_history.date_range")}
              </Text>
              {isDateFilterActive && (
                <Text
                  as={"button"}
                  type={"button"}
                  textStyle={"body-sm-medium"}
                  color={"#444440"}
                  _hover={{ color: "#1272EF" }}
                  onClick={clearDateRange}
                >
                  {t("path_pick:filter.clear_all")}
                </Text>
              )}
            </Flex>
            <PathPickOrderHistoryDateRangeFields
              startDate={startDate}
              endDate={endDate}
              onChangeStartDate={changeStartDate}
              onChangeEndDate={changeEndDate}
              direction={"column"}
            />
          </Flex>

          {/* 테이블 헤더 — 모바일 카드에는 열 개념이 없다 */}
          <Grid
            display={{ base: "none", md: "grid" }}
            templateColumns={ORDER_HISTORY_TEMPLATE_COLUMNS}
            gap={"16px"}
            padding={"10px 16px"}
            background={"#F7F9FA"}
            borderRadius={"8px"}
            border={"0.5px solid #D4D4D1"}
          >
            {(
              [
                "column_order_number",
                "column_products",
                "column_order_date",
                "column_quantity",
                "column_amount",
                "column_stage",
              ] as const
            ).map((key) => (
              <Text key={key} textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                {t(`path_pick:order_history.${key}`)}
              </Text>
            ))}
          </Grid>

          {/* 리스트 — 탭·페이지 전환 시에는 keepPreviousData로 이전 목록을 유지한다 */}
          {isPending ? (
            <CenteredSpinner padding={"40px 0"} />
          ) : total === 0 ? (
            <CustomNoResults title={t("path_pick:order_history.no_results")} />
          ) : (
            <Flex flexDirection={"column"} gap={"8px"}>
              {orders.map((order) => (
                <PathPickOrderHistoryListItem
                  key={order.id}
                  order={order}
                  onClick={() =>
                    navigate(`/pathpick/order-history/${order.id}`)
                  }
                />
              ))}
            </Flex>
          )}

          {total > 0 && (
            <PathPickPagination
              total={total}
              pageNumber={pageNumber}
              pageSize={pageSize}
              onChangePage={setPageNumber}
              onChangePageSize={setPageSize}
            />
          )}
        </Flex>
      </Flex>
    </Box>
  );
}

export default PathPickOrderHistoryPage;
